/**
 * ========================================
 * UNIFIED IMAGE ASSETS - Single Source of Truth
 * ========================================
 *
 * This file consolidates ALL image paths from both:
 * - /public/assets/ (legacy paths)
 * - /public/assetsV2/ (imported files)
 *
 * Usage:
 * import { IMAGES } from '@constants/images';
 *
 * <img src={IMAGES.LOGOS.KELP_APP.src} />
 * <img src={IMAGES.CHAINS.ETHEREUM} />
 * <img src={IMAGES.TOKENS.RSETH} />
 */

// ============================================
// IMPORTED ASSETS (from assets - merged from assetsV2)
// ============================================
import AppHeaderKernelLogo from "@assets/logo/app_header_kernel_logo.svg";
import GainAppLogo from "@assets/logo/gain_app_logo.svg";
import KelpAppLogo from "@assets/logo/kelp_app_logo.svg";
import KernelAppLogo from "@assets/logo/kernel_app_logo.svg";
import KernelDao from "@assets/logo/kernel_dao_logo.avif";
import FooterLogo from "@assets/logo/footer_logo.svg";
import KernelDaoOutlined from "@assets/logo/kernel_dao_outlined.svg";

import KernelSquareLogo from "@assets/brand/kernel_square.png";
import KelpSquareLogo from "@assets/brand/kelp_square.png";

import FaviconKernel from "@assets/favicons/Kernel.png";
import FaviconGain from "@assets/favicons/Gain.png";
import FaviconKelp from "@assets/favicons/Kelp.png";
import KernelWhite from "@assets/favicons/kernel-white.avif";
import KelpWhite from "@assets/favicons/kelp-white.avif";
import GainWhite from "@assets/favicons/gain-white.avif";

import GainOpportunitiesLargeDesktop from "@assets/illustrations/gain-op-xl.avif";
import GainOpportunitiesMobile from "@assets/illustrations/gain-op-mobile.avif";
import GainOpportunitiesTablet from "@assets/illustrations/gain-op-tablet.avif";
import GainOpportunitiesDesktop from "@assets/illustrations/gain-op-desktop.avif";
import KelpOpportunitiesLargeDesktop from "@assets/illustrations/kelp-op-xl.avif";
import KelpOpportunitiesMobile from "@assets/illustrations/kelp-op-mobile.avif";
import KelpOpportunitiesTablet from "@assets/illustrations/kelp-op-tablet.avif";
import KelpOpportunitiesDesktop from "@assets/illustrations/kelp-op-desktop.avif";
import Liquidity from "@assets/illustrations/liquidity.avif";
import Audited from "@assets/illustrations/audited.avif";
import Utility from "@assets/illustrations/utility.avif";
import Staking from "@assets/illustrations/restake.avif";

import KernelStakeIcon from "@assets/icons/stake.png";

// Import socials
import { XKernelLogo, XKelp } from "@assets/socials";

/**
 * ========================================
 * LOGOS & BRANDING
 * ========================================
 */
export const LOGOS = {
  // Primary Logos (imported objects)
  KERNEL_APP: KernelAppLogo,
  KELP_APP: KelpAppLogo,
  GAIN_APP: GainAppLogo,
  KERNEL_DAO: KernelDao,
  KERNEL_DAO_OUTLINED: KernelDaoOutlined,
  FOOTER: FooterLogo,
  APP_HEADER_KERNEL: AppHeaderKernelLogo,

  // Legacy path-based
  KELP: "/assets/kelp.svg",
} as const;

export const BRAND_LOGOS = {
  KERNEL_SQUARE: KernelSquareLogo,
  KELP_SQUARE: KelpSquareLogo,
} as const;

export const FAVICONS = {
  Kernel: FaviconKernel,
  Gain: FaviconGain,
  Kelp: FaviconKelp,
  KERNEL_WHITE: KernelWhite,
  KELP_WHITE: KelpWhite,
  GAIN_WHITE: GainWhite,
} as const;

export const ICONS = {
  KERNEL_STAKE: KernelStakeIcon,
} as const;

export const SOCIALS = {
  X_KERNEL: XKernelLogo,
  X_KELP: XKelp,
} as const;

/**
 * ========================================
 * CHAIN/NETWORK ICONS
 * ========================================
 */
export const CHAIN_ICON = {
  ETHEREUM: "/assets/chains/ethereum.svg",
  ARBITRUM: "/assets/bridge/arbitrum.svg",
  OPTIMISM: "/assets/chains/optimism.svg",
  BASE: "/assets/chains/base.svg",
  AVALANCHE: "/assets/chains/avalanche.png",
  BSC: "/assets/chains/bsc.svg",
  POLYGON: "/assets/chains/polygon.svg",
  LINEA: "/assets/chains/linea.svg",
  MODE: "/assets/chains/mode.svg",
  BLAST: "/assets/bridge/blast.svg",
  SCROLL: "/assets/chains/scroll.svg",
  ZKSYNC: "/assets/chains/zksync.svg",
  XLAYER: "/assets/chains/xlayer.svg",
  MANTA: "/assets/bridge/manta-pacific.svg",
  ZIRCUIT: "/assets/chains/zircuit.svg",
  TAC: "/assets/chains/tac.svg",
  UNICHAIN: "/assets/chains/unichain.svg",
  SONIC: "/assets/chains/sonic.png",
  PLASMA: "/assets/chains/plasma.png",
} as const;

/**
 * ========================================
 * TOKEN ICONS
 * ========================================
 */
export const TOKEN_ICONS = {
  // Main tokens
  RSETH: "/assets/rseth.png",
  WRSETH: "/assets/wrseth.svg",
  AGETH: "/assets/ageth.svg",
  WAGETH: "/assets/ageth.svg",
  ETH: "/assets/eth.svg",
  WETH: "/assets/weth.svg",
  STETH: `/assets/stETH.svg`,
  WSTETH: "/assets/wsteth.svg",
  ETHX: "/assets/ethx.svg",
  SFRXETH: "/assets/sfrxeth.svg",

  // Stablecoins
  USDT: "/assets/usdt.svg",
  USDC: "/assets/usdc.svg",

  // Others
  PLACEHOLDER: "/assets/placeholder.svg",
} as const;

/**
 * ========================================
 * VAULT ICONS
 * ========================================
 */
export const VAULT_ICONS = {
  HGETH: "/assets/vaults/hgeth.svg",
  AGETH: "/assets/agETH.png",
  GGETH: "/assets/vaults/ggETH.svg",
  SBUSD: "/assets/vaults/stable.svg",
  MOVEMENT: "/assets/vaults/movementL2logo.png",
  TAC_BTC: "/assets/vaults/tacBtc.svg",
  TAC_ETH: "/assets/vaults/tacEth.svg",
} as const;

/**
 * ========================================
 * WALLET ICONS
 * ========================================
 */
export const WALLET_ICONS = {
  INJECTED: "/assets/wallets/injected.svg",
  METAMASK: "/assets/wallets/mm.png",
  COINBASE: "/assets/wallets/cbw.png",
  WALLET_CONNECT: "/assets/wallets/wc.png",
  TRUST_WALLET: "/assets/wallets/trust.jpeg",
  SAFEPAL: "/assets/wallets/safepal.jpeg",
  MATH_WALLET: "/assets/wallets/math.png",
  LEDGER: "/assets/wallets/ledger.png",
  RABBY: "/assets/wallets/rabby.jpeg",
  OKX: "/assets/wallets/okex.png",
} as const;

/**
 * ========================================
 * SIDEBAR NAVIGATION ICONS
 * ========================================
 */
export const SIDEBAR_ICONS = {
  GAIN: "/assets/sidebar/gain.svg",
  AVS: "/assets/sidebar/avs.svg",
  AVS_PNG: "/assets/sidebar/avs.png",
  RESTAKE: "/assets/sidebar/restake.svg",
  BRIDGE: "/assets/sidebar/bridge.svg",
  CLAIM: "/assets/sidebar/claim.svg",
  CLAIM_EL: "/assets/sidebar/claim-el.svg",
  CLAIM_KEP: "/assets/sidebar/claim-kep.svg",
  DEFI: "/assets/sidebar/defi.svg",
  DASHBOARD: "/assets/sidebar/dashboard.svg",
  AIRDROP_VAULT: "/assets/sidebar/airdrop-vault.svg",
  ALL_VAULTS: "/assets/sidebar/all-vaults.svg",
  REWARD: "/assets/sidebar/reward.svg",
  NEW_TAG: "/assets/sidebar/newTag.svg",
  MOVEMENT_VAULT: "/assets/sidebar/movement-vault.svg",

  // Active/Inactive states
  ACTIVE: {
    DROP_HALF: "/assets/sidebar/active/dropHalf.svg",
    BRIDGE: "/assets/sidebar/active/bridge.svg",
    VAULT: "/assets/sidebar/active/vault.svg",
    SPHERE: "/assets/sidebar/active/sphere.svg",
    TAC_BTC: "/assets/sidebar/active/tacBtc.svg",
    TAC_ETH: "/assets/sidebar/active/tacEth.svg",
  },
  INACTIVE: {
    DROP_HALF: "/assets/sidebar/inActive/dropHalf.svg",
    BRIDGE: "/assets/sidebar/inActive/bridge.svg",
    VAULT: "/assets/sidebar/inActive/vault.svg",
    SPHERE: "/assets/sidebar/inActive/sphere.svg",
    PARACHUTE: "/assets/sidebar/inActive/parachute.svg",
    PLANT: "/assets/sidebar/inActive/plant.svg",
    CHART_PIE: "/assets/sidebar/inActive/chartPie.svg",
    FLIP_HORIZONTAL: "/assets/sidebar/inActive/flipHorizontal.svg",
    TAC_BTC: "/assets/sidebar/inActive/tacBtc.svg",
    TAC_ETH: "/assets/sidebar/inActive/tacEth.svg",
    CLOCK_COUNTDOWN: "/assets/sidebar/inActive/clockCountdown.svg",
    GGETH_SIDENAV: "/assets/sidebar/inActive/ggETH-sidenav.svg",
  },
} as const;

/**
 * ========================================
 * ILLUSTRATIONS & HERO IMAGES
 * ========================================
 */
export const ILLUSTRATIONS = {
  // Gain Opportunities (Responsive)
  GAIN_OPPORTUNITIES: {
    MOBILE: GainOpportunitiesMobile,
    TABLET: GainOpportunitiesTablet,
    DESKTOP: GainOpportunitiesDesktop,
    LARGE_DESKTOP: GainOpportunitiesLargeDesktop,
  },

  // Kelp Opportunities (Responsive)
  KELP_OPPORTUNITIES: {
    MOBILE: KelpOpportunitiesMobile,
    TABLET: KelpOpportunitiesTablet,
    DESKTOP: KelpOpportunitiesDesktop,
    LARGE_DESKTOP: KelpOpportunitiesLargeDesktop,
  },

  // Feature illustrations
  LIQUIDITY: Liquidity,
  AUDITED: Audited,
  UTILITY: Utility,
  STAKING: Staking,
} as const;

/**
 * ========================================
 * TRANSACTION/STATUS ICONS
 * ========================================
 */
export const TRANSACTION_ICONS = {
  ERROR: "/assets/transactions/error.svg",
  SUCCESS: "/assets/transactions/success.svg",
  PENDING: "/assets/transactions/pending.svg",
} as const;

/**
 * ========================================
 * ECOSYSTEM PARTNERS
 * ========================================
 */
export const ECOSYSTEM_ICONS = {
  // AVS Partners
  AVS: {
    WITNESS: "/assets/ecosystem/avs/witness.svg",
    AUTOMATA: "/assets/ecosystem/avs/automata.svg",
    EIGEN: "/assets/ecosystem/avs/eigen.svg",
    EORACLE: "/assets/ecosystem/avs/eoracle.svg",
    BREVIS: "/assets/ecosystem/avs/brevis.svg",
    CYBERMATCH: "/assets/ecosystem/avs/cybermatch.svg",
    OPENLAYER: "/assets/ecosystem/avs/openlayer.svg",
    HYPERLANE: "/assets/ecosystem/avs/hyperlane.svg",
  },

  // Operators
  OPERATORS: {
    LUGANODES: "/assets/ecosystem/operators/luganodes.svg",
    OMAKASE: "/assets/ecosystem/operators/omakase.svg",
    KLIN: "/assets/ecosystem/operators/klin.svg",
    ALLNODES: "/assets/ecosystem/operators/allnodes.svg",
  },

  // L2 Chains
  L2: {
    XLAYER: "/assets/ecosystem/l2/xlayer.svg",
    ZKSYNC: "/assets/ecosystem/l2/zk.svg",
    MODE: "/assets/ecosystem/l2/mode.svg",
    LINEA: "/assets/ecosystem/l2/linea.svg",
    BLAST: "/assets/ecosystem/l2/blast.svg",
    SCROLL: "/assets/ecosystem/l2/scroll.svg",
    ARBITRUM: "/assets/ecosystem/l2/arb.svg",
    OPTIMISM: "/assets/ecosystem/l2/op.svg",
    BASE: "/assets/ecosystem/l2/base.svg",
  },

  // DeFi Protocols
  DEFI: {
    AMBIENT: "/assets/ecosystem/defi/ambient.svg",
    BALANCER: "/assets/ecosystem/defi/balancer.svg",
    UNISWAP: "/assets/ecosystem/defi/uni.svg",
    CURVE: "/assets/ecosystem/defi/curve.svg",
    COMPOUND: "/assets/ecosystem/defi/compound.svg",
    PENDLE: "/assets/ecosystem/defi/pendle.svg",
    THRUSTER: "/assets/ecosystem/defi/thruster.svg",
    CAMELOT: "/assets/ecosystem/defi/camelot.svg",
    ZEROLEND: "/assets/ecosystem/defi/zerolend.svg",
    LAYERBANK: "/assets/ecosystem/defi/layerbank.svg",
    GEARBOX: "/assets/ecosystem/defi/gearbox.svg",
  },
} as const;

/**
 * ========================================
 * INSTITUTIONS & INVESTORS
 * ========================================
 */
export const INSTITUTIONS_ICONS = {
  HERO: {
    ILLUSTRATION: "/assets/institutions/hero/illustration.svg",
    ILLUSTRATION_MOBILE: "/assets/institutions/hero/illustration-mobile.svg",
  },

  INTRO: {
    YIELD: "/assets/institutions/intro/yield.svg",
    COINS: "/assets/institutions/intro/coins.svg",
    SECURITY: "/assets/institutions/intro/security.svg",
  },

  WHY: {
    LIQUIDITY: "/assets/institutions/why/liquidity.svg",
    LENDING: "/assets/institutions/why/lending.svg",
    YIELD_OPTIMIZER: "/assets/institutions/why/yield-optimiser.svg",
  },

  AUDITS: {
    TIMELOCK: "/assets/institutions/audits/time-lock.svg",
    MULTISIG: "/assets/institutions/audits/multi-sign.svg",
    CODE4RENA: "/assets/institutions/audits/code4rena.svg",
    MIXBYTES: "/assets/institutions/audits/mix-bytes.svg",
    SIGMA_PRIME: "/assets/institutions/audits/sigma-prime.svg",
  },

  INVESTMENTS: {
    DWF_LABS: "/assets/institutions/investments/dwf-labs.svg",
    LONGHASH: "/assets/institutions/investments/longhash.svg",
    HYPERSPHERE: "/assets/institutions/investments/hypersphere.svg",
    BANKLESS: "/assets/institutions/investments/bankless.svg",
    COINSEEKER: "/assets/institutions/investments/coinseeker.svg",
    DRAPER: "/assets/institutions/investments/draper.svg",
    LASER_DIGITAL: "/assets/institutions/investments/laser-digital.svg",
    AVID: "/assets/institutions/investments/avid.svg",
    CLUSTER: "/assets/institutions/investments/cluster.svg",
    GSR: "/assets/institutions/investments/gsr.svg",
    HTX: "/assets/institutions/investments/htx.svg",
    SCB: "/assets/institutions/investments/scb.svg",
  },

  CONTACT: {
    BACKGROUND: "/assets/institutions/contact/bg.svg",
  },
} as const;

/**
 * ========================================
 * CLAIM/AIRDROP ASSETS
 * ========================================
 */
export const CLAIM_ICONS = {
  KERNEL: "/assets/claim/kernel_32.png",
  EIGEN: "/assets/claim/eigen.svg",
} as const;

/**
 * ========================================
 * COMMON UI ICONS
 * ========================================
 */
export const UI_ICONS = {
  SEARCH: "/assets/search.svg",
  PLACEHOLDER: "/assets/placeholder.svg",
  KERNEL_STAKE: KernelStakeIcon,
} as const;

/**
 * ========================================
 * BANNERS
 * ========================================
 */
export const BANNERS = {
  STABLE_VAULT_V2: "/assets/stable-vault-banner-v2.svg",
} as const;

/**
 * ========================================
 * MAIN UNIFIED EXPORT
 * ========================================
 */
export const IMAGES = {
  LOGOS,
  BRAND: BRAND_LOGOS,
  FAVICONS,
  ICONS,
  SOCIALS,
  CHAINS: CHAIN_ICON,
  TOKENS: TOKEN_ICONS,
  VAULTS: VAULT_ICONS,
  WALLETS: WALLET_ICONS,
  SIDEBAR: SIDEBAR_ICONS,
  ILLUSTRATIONS,
  TRANSACTIONS: TRANSACTION_ICONS,
  ECOSYSTEM: ECOSYSTEM_ICONS,
  INSTITUTIONS: INSTITUTIONS_ICONS,
  CLAIM: CLAIM_ICONS,
  UI: UI_ICONS,
  BANNERS,
} as const;

/**
 * ========================================
 * HELPER FUNCTIONS
 * ========================================
 */

/**
 * Get chain icon by chain ID
 */
export const getChainIconById = (chainId: number): string => {
  const chainIconMap: Record<number, string> = {
    1: CHAIN_ICON.ETHEREUM,
    10: CHAIN_ICON.OPTIMISM,
    56: CHAIN_ICON.BSC,
    137: CHAIN_ICON.POLYGON,
    8453: CHAIN_ICON.BASE,
    42161: CHAIN_ICON.ARBITRUM,
    43114: CHAIN_ICON.AVALANCHE,
    59144: CHAIN_ICON.LINEA,
    34443: CHAIN_ICON.MODE,
    81457: CHAIN_ICON.BLAST,
    534352: CHAIN_ICON.SCROLL,
    324: CHAIN_ICON.ZKSYNC,
    196: CHAIN_ICON.XLAYER,
    169: CHAIN_ICON.MANTA,
    48900: CHAIN_ICON.ZIRCUIT,
    239: CHAIN_ICON.TAC,
    130: CHAIN_ICON.UNICHAIN,
    146: CHAIN_ICON.SONIC,
    9745: CHAIN_ICON.PLASMA,
  };

  return chainIconMap[chainId] || TOKEN_ICONS.PLACEHOLDER;
};

/**
 * Get responsive illustration based on breakpoint
 */
export const getResponsiveIllustration = (
  type: "GAIN" | "KELP",
  breakpoint: "mobile" | "tablet" | "desktop" | "largeDesktop"
) => {
  const illustrations =
    type === "GAIN"
      ? ILLUSTRATIONS.GAIN_OPPORTUNITIES
      : ILLUSTRATIONS.KELP_OPPORTUNITIES;

  const key =
    breakpoint === "largeDesktop" ? "LARGE_DESKTOP" : breakpoint.toUpperCase();
  return illustrations[key as keyof typeof illustrations];
};

/**
 * ========================================
 * TYPE EXPORTS
 * ========================================
 */
export type ImageCategory = keyof typeof IMAGES;
export type ChainIconKey = keyof typeof CHAIN_ICON;
export type TokenIconKey = keyof typeof TOKEN_ICONS;
export type WalletIconKey = keyof typeof WALLET_ICONS;
export type VaultIconKey = keyof typeof VAULT_ICONS;
export type LogoKey = keyof typeof LOGOS;

/**
 * Backward compatibility alias from old assets.ts
 * @deprecated Use IMAGES instead of ASSETS
 */
export const ASSETS = IMAGES;

/**
 * Default export for convenience
 */
export default IMAGES;
